/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChevronRight, ArrowUpRight, Instagram, Landmark, Award, BookOpen } from "lucide-react";
import Navigation from "./components/Navigation";
import Lookbook from "./components/Lookbook";
import StoriesGrid from "./components/StoriesGrid";
import InquiryAtelier from "./components/InquiryAtelier";
import About from "./components/About";
import HowItWorks from "./components/HowItWorks";
import TermsPrivacy from "./components/TermsPrivacy";

export type RoseDemoView = "home" | "lookbook" | "stories" | "inquiry" | "about" | "how-it-works" | "terms-privacy";
export type RoseLegalTab = "terms" | "privacy" | "contract";

interface AppProps {
  basePath?: string;
  initialView?: RoseDemoView;
  initialLegalTab?: RoseLegalTab;
}

function routeForView(view: RoseDemoView, basePath: string, legalTab?: RoseLegalTab) {
  const base = basePath || "";
  if (view === "home") return base || "/";
  if (view === "lookbook") return `${base}/gallery`;
  if (view === "stories") return `${base}/stories`;
  if (view === "inquiry") return `${base}/contact`;
  if (view === "about") return `${base}/about`;
  if (view === "how-it-works") return `${base}/how-it-works`;
  if (legalTab === "privacy") return `${base}/privacy`;
  if (legalTab === "contract") return `${base}/contract`;
  return `${base}/terms`;
}

export default function App({ basePath = "", initialView = "home", initialLegalTab = "terms" }: AppProps) {
  const navigate = useNavigate();
  const [view, setView] = useState<RoseDemoView>(initialView);
  const [legalTab, setLegalTab] = useState<RoseLegalTab>(initialLegalTab);

  useEffect(() => {
    setView(initialView);
    setLegalTab(initialLegalTab);
  }, [initialView, initialLegalTab]);

  const showView = (nextView: RoseDemoView, nextLegalTab?: RoseLegalTab) => {
    if (nextLegalTab) setLegalTab(nextLegalTab);
    setView(nextView);
    navigate(routeForView(nextView, basePath, nextLegalTab));
  };

  // Scroll to top upon navigating tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [view]);

  return (
    <div className="min-h-screen bg-[#faf9f6]/95 text-[#121212] font-sans antialiased relative">
      {/* Absolute Cinematic Grain Overlay */}
      <div className="grain-overlay absolute inset-0 z-40 pointer-events-none opacity-40"></div>

      {/* Navigation Suite */}
      <Navigation onNavClick={(v) => showView(v)} currentView={view} />

      {/* Main Views switcher */}
      <main className="relative z-10">
        
        {view === "home" && (
          <div className="animate-fade-in">
            {/* Section 1: Cinematic Editorial Cover */}
            <section className="relative h-[90vh] flex items-center justify-center bg-black overflow-hidden border-b border-[#121212]/10">
              {/* Backing Luxury Image */}
              <div className="absolute inset-0 opacity-70">
                <img
                  src="/rose/demo/hero-vogue-wedding.png"
                  alt="Vogue Editorial Cover"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover transition-transform duration-10000 ease-[cubic-bezier(0.16,1,0.3,1)] scale-105 hover:scale-100"
                />
              </div>

              {/* Gradient Scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40"></div>

              {/* Text Highlights */}
              <div className="relative text-center max-w-4xl px-6 py-12 flex flex-col items-center justify-center text-white space-y-6">
                <h1 className="font-serif text-[44px] sm:text-[76px] lg:text-[95px] font-extralight tracking-tight leading-none text-[#F8F5EF] select-none">
                  Weddings Worth Waiting For.
                </h1>

                <div className="w-24 h-[1px] bg-[#C89848]/65"></div>

                <p className="font-sans font-semibold text-xs sm:text-sm text-[#F8F5EF] max-w-lg uppercase tracking-[0.25em] leading-relaxed">
                  For Couples Who Want the Best.
                </p>



                {/* Cover CTA */}
                <div className="pt-6">
                  <button
                    id="btn-commission-cta"
                    onClick={() => showView("inquiry")}
                    className="bg-[#4A1722] text-[#F8F5EF] hover:bg-[#241018] text-[11px] uppercase tracking-[0.25em] py-4.5 px-12 transition duration-500 font-display cursor-pointer min-h-[44px] flex items-center justify-center select-none font-semibold border-none"
                  >
                    JOIN THE WAITLIST
                  </button>
                </div>
              </div>

              {/* Decorative Corner coordinates */}
              <div className="absolute bottom-6 left-6 hidden sm:block text-[9px] uppercase tracking-widest text-zinc-400 font-display">
                <span>COORD: 43.6150° N, 1.4442° E // SW PRESET 2026</span>
              </div>
              <div className="absolute bottom-6 right-6 hidden sm:block text-[9px] uppercase tracking-widest text-zinc-400 font-display">
                <span>PLATE NO. 081 // MONOCHROME DETAIL</span>
              </div>
            </section>

            {/* Section 2: The House Manifesto / Dual Column */}
            <section className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
              {/* Left text manifesto (7 columns) */}
              <div className="md:col-span-7 space-y-6">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#c4a482] font-semibold flex items-center gap-1.5">
                  <Landmark size={11} /> The Photographic Medium
                </span>
                <h2 className="font-serif text-[42px] sm:text-[54px] font-extralight tracking-tight leading-tight text-[#121212]">
                  We do not merely document celebrations. We create classic, high-end photographic campaigns.
                </h2>
                <div className="w-16 h-[1px] bg-[#c4a482]"></div>
                <p className="font-mono text-[11px] text-[#7f7d78] uppercase tracking-widest leading-relaxed">
                  OUR PHILOSOPHY IS ROOTED IN HONEST STORYTELLING AND EXCELLENT CRAFTSMANSHIP. WE USE INCREDIBLE DIGITAL TOOLS AND CAREFUL COMPOSITIONS TO PRESERVE REAL MOMENTS AND GENUINE EMOTIONS.
                </p>
                <p className="font-serif text-[16px] text-neutral-600 leading-relaxed font-light">
                  Our goal is to make you feel comfortable, present, and completely yourself. We focus on natural lighting, spontaneous movement, and genuine connection—capturing everything from quiet glances to uninhibited laughter and the rich details of your day. Every photograph we deliver is carefully colored and crafted to serve as an honest, lasting record for your family's legacy.
                </p>
              </div>

              {/* Right Side Image teaser (5 columns) */}
              <div className="md:col-span-5 bg-white border border-black/5 p-4 self-stretch flex flex-col justify-between">
                <div className="aspect-[3/4] overflow-hidden bg-[#e2dfd9]">
                  <img
                    src="/rose/demo/amalfi-editorial-bride.png"
                    alt="Amalfi Terrace Highlight"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                  />
                </div>
                <div className="pt-4 flex justify-between items-baseline">
                  <div>
                    <span className="block font-serif text-[16px] font-light text-[#121212]">Ravello, Villa Cimbrone</span>
                    <span className="text-[8px] uppercase tracking-widest text-[#8c8a81]">Shot No. 129 // Leica M11-P ASPH</span>
                  </div>
                  <button
                    id="btn-amalfi-story"
                    onClick={() => showView("stories")}
                    className="p-2 border border-black/10 rounded-full hover:border-black transition text-[#121212]"
                  >
                    <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
            </section>

            {/* Section 3: The Custom Interactive Teasers / Bento Row */}
            <section className="bg-black text-[#faf9f6] py-24 px-6 relative overflow-hidden">
              <div className="max-w-7xl mx-auto space-y-12">
                
                {/* Section Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 border-b border-white/10 pb-6">
                  <div>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#c4a482] block mb-2 font-display">
                      Modern Luxury Options
                    </span>
                    <h3 className="font-serif text-[36px] sm:text-[48px] font-extralight tracking-tight leading-none text-white">
                      Bespoke Interactive Portals
                    </h3>
                  </div>
                  <p className="text-zinc-400 text-xs uppercase tracking-widest max-w-sm font-light">
                    Interact directly with our custom moodboard builders and detailed destination timelines.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  
                  {/* Card 1: Lookbook teaser */}
                  <div
                    id="bento-lookbook"
                    onClick={() => showView("lookbook")}
                    className="border border-white/10 bg-[#161616] p-8 flex flex-col justify-between h-80 group cursor-pointer hover:border-[#c4a482]/60 transition-all duration-500"
                  >
                    <div className="space-y-4">
                      <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#c4a482]">
                        <Camera size={16} />
                      </div>
                      <h4 className="font-serif text-[22px] font-light text-white leading-tight">
                        Catalog Lookbook
                      </h4>
                      <p className="text-zinc-400 text-[11px] leading-relaxed font-light uppercase tracking-wider">
                        Explore our categorized portfolio grid. Zoom in on frame metadata including lens types, focal lengths, and camera formulations.
                      </p>
                    </div>

                    <button
                      id="card-cta-lookbook"
                      className="text-[10.5px] uppercase tracking-[0.2em] font-medium text-white group-hover:text-[#c4a482] transition duration-300 flex items-center gap-1 self-start"
                    >
                      <span>Browse Grid</span>
                      <ChevronRight size={12} />
                    </button>
                  </div>

                  {/* Card 2: Stories teaser */}
                  <div
                    id="bento-stories"
                    onClick={() => showView("stories")}
                    className="border border-white/10 bg-[#161616] p-8 flex flex-col justify-between h-80 group cursor-pointer hover:border-[#c4a482]/60 transition-all duration-500"
                  >
                    <div className="space-y-4">
                      <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#c4a482]">
                        <BookOpen size={16} />
                      </div>
                      <h4 className="font-serif text-[22px] font-light text-white leading-tight">
                        Destination Chronologies
                      </h4>
                      <p className="text-zinc-400 text-[11px] leading-relaxed font-light uppercase tracking-wider">
                        Step inside elite high-society weddings. Check styling gowns, tailored suits, floral directions, and chronological agendas.
                      </p>
                    </div>

                    <button
                      id="card-cta-stories"
                      className="text-[10.5px] uppercase tracking-[0.2em] font-medium text-white group-hover:text-[#c4a482] transition duration-300 flex items-center gap-1 self-start"
                    >
                      <span>Read Chronicles</span>
                      <ChevronRight size={12} />
                    </button>
                  </div>

                </div>

              </div>
            </section>

            {/* Section 4: Why Couples Choose The Rose Photo */}
            <section className="bg-[#FAF9F6] py-24 px-6 border-t border-[#4A1722]/10" id="why-choose-us-section">
              <div className="max-w-5xl mx-auto">
                <div className="max-w-3xl mx-auto text-center mb-16 space-y-3">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#C89848] font-bold flex items-center justify-center gap-1.5 mb-2">
                    <Award size={11} /> The Atelier Difference
                  </span>
                  <h2 className="font-serif text-[42px] sm:text-[54px] font-extralight tracking-tight leading-none text-[#241018]">
                    Why Couples Choose The Rose Photo
                  </h2>
                  <p className="font-serif italic text-sm text-[#4A1722] font-semibold">
                    Pro photos, zero hassle. We handle the details.
                  </p>
                  <div className="w-16 h-[1px] bg-[#4A1722]/30 mx-auto my-6"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
                  {/* Left Highlight Card */}
                  <div className="border border-[#4A1722]/15 p-10 bg-[#F2ECE3] space-y-6 hover:border-[#4A1722]/40 transition duration-500 hover:shadow-md">
                    <div className="w-10 h-10 rounded-full bg-[#F8F5EF] border border-[#4A1722]/15 flex items-center justify-center text-[#4A1722] font-semibold font-mono text-xs">
                      01
                    </div>
                    <h3 className="font-serif text-[24px] font-semibold text-[#241018] tracking-tight">
                      Limited Availability
                    </h3>
                    <p className="text-sm font-serif text-neutral-700 leading-relaxed font-light">
                      Our calendar is intentionally kept small to preserve a highly personal experience.
                    </p>
                  </div>

                  {/* Right Highlight Card */}
                  <div className="border border-[#4A1722]/15 p-10 bg-[#F2ECE3] space-y-6 hover:border-[#4A1722]/40 transition duration-500 hover:shadow-md">
                    <div className="w-10 h-10 rounded-full bg-[#F8F5EF] border border-[#4A1722]/15 flex items-center justify-center text-[#4A1722] font-semibold font-mono text-xs">
                      02
                    </div>
                    <h3 className="font-serif text-[24px] font-semibold text-[#241018] tracking-tight">
                      Personal Experience
                    </h3>
                    <p className="text-sm font-serif text-neutral-700 leading-relaxed font-light">
                      We take the time to get to know you, your story, and what matters most to you.
                    </p>
                  </div>
                </div>

                {/* Aesthetic footer notice to coordinate waitlist admissions */}
                <div className="text-center mt-16 space-y-4">
                  <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#4A1722] font-sans">
                    We take a limited number of weddings each year. Join our waitlist today.
                  </p>
                  <button
                    id="btn-why-choose-waitlist"
                    onClick={() => showView("inquiry")}
                    className="inline-flex bg-[#4A1722] text-[#F8F5EF] hover:bg-[#241018] text-[10.5px] uppercase tracking-[0.25em] py-4 px-12 transition duration-500 font-display cursor-pointer font-semibold min-h-[44px] items-center justify-center border-none"
                  >
                    JOIN THE WAITLIST
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Modular View mounts */}
        {view === "lookbook" && <Lookbook />}
        {view === "stories" && <StoriesGrid />}
        {view === "inquiry" && <InquiryAtelier />}
        {view === "about" && <About onJoinWaitlist={() => showView("inquiry")} />}
        {view === "how-it-works" && <HowItWorks onJoinWaitlist={() => showView("inquiry")} onExploreLookbook={() => showView("lookbook")} />}
        {view === "terms-privacy" && (
          <TermsPrivacy
            activeTab={legalTab}
            onTabChange={(tab) => {
              setLegalTab(tab);
              navigate(routeForView("terms-privacy", basePath, tab));
            }}
            onBackToHome={() => showView("home")}
          />
        )}

      </main>

      {/* Footer Suite */}
      <footer className="bg-[#121212] text-[#faf9f6] pt-20 pb-12 px-6 border-t border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 pb-12 border-b border-white/10">
          
          {/* Column 1: Brand & Aesthetic Manifesto */}
          <div className="space-y-4 md:col-span-1.5">
            <h4 className="font-serif text-[22px] tracking-widest uppercase font-light text-[#faf9f6]">
              THE ROSE PHOTO
            </h4>
            <span className="block text-[8px] uppercase tracking-[0.4em] text-[#c4a482] -mt-2">
              vogue • editorial • fine art
            </span>
            <p className="text-xs text-zinc-400 font-light leading-relaxed pt-2 max-w-sm">
              We travel worldwide to capture unscripted haute-fashion romance. Deeply inspired by cinematic lighting, high-contrast YSL styling, and architectural symmetry.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-4">
            <span className="block text-[9px] uppercase tracking-widest text-zinc-500 font-semibold font-display">
              Atelier Pages
            </span>
            <ul className="space-y-2 text-xs font-light text-zinc-400 font-sans uppercase tracking-widest">
              <li>
                <button id="foot-nav-home" onClick={() => showView("home")} className="hover:text-white transition cursor-pointer">
                  Entrance / Home
                </button>
              </li>
              <li>
                <button id="foot-nav-lookbook" onClick={() => showView("lookbook")} className="hover:text-white transition cursor-pointer">
                  Portfolio Lookbook
                </button>
              </li>
              <li>
                <button id="foot-nav-stories" onClick={() => showView("stories")} className="hover:text-white transition cursor-pointer">
                  Campaign Stories
                </button>
              </li>
              <li>
                <button id="foot-nav-about" onClick={() => showView("about")} className="hover:text-white transition cursor-pointer">
                  About Ethos
                </button>
              </li>
              <li>
                <button id="foot-nav-how-it-works" onClick={() => showView("how-it-works")} className="hover:text-white transition cursor-pointer">
                  How It Works
                </button>
              </li>
              <li>
                <button id="foot-nav-inquiry" onClick={() => showView("inquiry")} className="hover:text-white transition cursor-pointer text-[#C89848] font-semibold">
                  JOIN THE WAITLIST
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Accredited Portals */}
          <div className="space-y-4">
            <span className="block text-[9px] uppercase tracking-widest text-[#C89848] font-semibold font-display">
              Accredited Portals
            </span>
            <ul className="space-y-2 text-xs font-light text-zinc-400 font-sans uppercase tracking-widest">
              <li>
                <a
                  href="https://www.zola.com/wedding-vendors/wedding-photographers/the-rose-photo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition duration-300 flex items-center space-x-1.5"
                >
                  <span>Zola Profile</span>
                  <ArrowUpRight size={10} className="text-[#C89848]" />
                </a>
              </li>
              {/* Hidden until these profiles are ready.
              <li>
                <a
                  href="https://www.theknot.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition duration-300 flex items-center space-x-1.5"
                >
                  <span>The Knot</span>
                  <ArrowUpRight size={10} className="text-[#C89848]" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.weddingwire.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition duration-300 flex items-center space-x-1.5"
                >
                  <span>WeddingWire</span>
                  <ArrowUpRight size={10} className="text-[#C89848]" />
                </a>
              </li>
              */}
            </ul>
          </div>

          {/* Column 4: Social / Copyright */}
          <div className="space-y-4">
            <span className="block text-[9px] uppercase tracking-widest text-zinc-500 font-semibold font-display">
              Electronic Socials
            </span>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/therosephoto/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="The Rose Photo on Instagram"
                className="inline-flex items-center justify-center h-9 w-9 border border-white/15 text-zinc-400 hover:text-[#c4a482] hover:border-[#c4a482] transition duration-300"
              >
                <Instagram size={14} />
              </a>
            </div>
            
            <div className="pt-4 space-y-1">
              <span className="block text-[9px] text-zinc-600 uppercase tracking-widest font-display font-light">
                Secure SSL Protected
              </span>
              <span className="block text-[8px] text-zinc-600 uppercase tracking-widest font-display font-light">
                Leica authorized laboratory partner
              </span>
            </div>
          </div>

        </div>

        {/* Lower copyright */}
        <div className="max-w-7xl mx-auto pt-10 flex flex-col sm:flex-row justify-between items-center text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <span>The Rose Photo House © 2026 • All Rights Reserved</span>
            <div className="flex space-x-4">
              <button 
                onClick={() => showView("terms-privacy", "terms")}
                className="hover:text-white transition cursor-pointer font-sans uppercase tracking-[0.2em] bg-transparent border-none p-0 text-zinc-500"
              >
                Terms of Service
              </button>
              <span className="text-zinc-800 select-none">•</span>
              <button 
                onClick={() => showView("terms-privacy", "privacy")}
                className="hover:text-white transition cursor-pointer font-sans uppercase tracking-[0.2em] bg-transparent border-none p-0 text-zinc-500"
              >
                Privacy Policy
              </button>
              <span className="text-zinc-800 select-none">•</span>
              <button
                onClick={() => showView("terms-privacy", "contract")}
                className="hover:text-white transition cursor-pointer font-sans uppercase tracking-[0.2em] bg-transparent border-none p-0 text-zinc-500"
              >
                Contract
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
